import { MoreHorizontal } from "lucide-react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "./Button"
import { Dropdown } from "./Dropdown"

export interface RowAction {
  label: string
  icon?: ReactNode
  onClick: () => void
  /** Moves the row somewhere it can be lost from (the trash). Menu items only. */
  danger?: boolean
  loading?: boolean
}

/**
 * One visible action per row, the rest behind "⋯" (PRD-60 chunk B). Rows used
 * to carry four 12px text buttons and a red "Eliminar" each — ten red words
 * down a subjects list, a 445px action column, 160 Tab stops for 40 rows.
 *
 * The visible action is the one the row's state asks for (Reenviar on a
 * pending invitation, Restaurar in the trash). Every name carries the row's
 * subject, so a screen reader hears "Editar, Ana López", not ten "Editar".
 */
export function RowActions({
  subject,
  primary,
  items,
}: {
  /** Who or what the row is — appended to every accessible name. */
  subject: string
  primary?: RowAction
  /** Pass `[]` (not nothing) in a list where other rows have a menu: the
   *  slot is kept so the visible actions line up. */
  items?: RowAction[]
}) {
  const { t } = useTranslation("common")
  return (
    <div className="inline-flex items-center gap-2">
      {primary && (
        // Ghost: a column of outlined boxes read as a grid of buttons, not
        // as rows of people with one thing to do each.
        <Button
          variant="ghost"
          leftIcon={primary.icon}
          loading={primary.loading}
          onClick={primary.onClick}
        >
          {primary.label}
          <span className="sr-only">, {subject}</span>
        </Button>
      )}
      {items?.length === 0 && <span aria-hidden className="h-11 w-11 md:h-9 md:w-9" />}
      {items && items.length > 0 && (
        <Dropdown
          groups={[
            { items: items.filter((i) => !i.danger).map(toItem) },
            { items: items.filter((i) => i.danger).map(toItem) },
          ].filter((g) => g.items.length > 0)}
          renderTrigger={({ ref, open: _open, ...props }) => (
            <button
              ref={ref}
              type="button"
              aria-label={t("row.more", { name: subject })}
              {...props}
              className="rounded-control text-ink-muted hover:bg-inset hover:text-ink inline-flex h-11 w-11 items-center justify-center transition-colors duration-150 md:h-9 md:w-9"
            >
              <MoreHorizontal className="h-5 w-5" aria-hidden />
            </button>
          )}
        />
      )}
    </div>
  )
}

function toItem(action: RowAction) {
  return {
    label: action.label,
    icon: action.icon,
    onClick: action.onClick,
    variant: action.danger ? ("danger" as const) : ("default" as const),
  }
}
